/**
 * Publicar, con la nota previa.
 *
 * Antes de subir nada, el mismo publicador en seco (GET /:id/review) más lo
 * que quedó a medias en Salud del sitio: qué se sube, qué no cambió y no se
 * toca, qué cambió en el servidor fuera de EvoHub, textos propuestos sin
 * aplicar, decisiones pendientes, arreglos automáticos sin hacer. Si hay algo,
 * una nota con «Corregir primero» / «Publicar igual»; si no, publica directo.
 * Vive en un hook porque el botón está en la fila de pestañas del proyecto
 * (Eduardo, 2026-09-06: "el botón Publish ya está arriba").
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button.js';
import { Modal } from '../../ui/Modal.js';
import { useToast } from '../../ui/Toast.js';
import type { ProjectRow } from '../../../hooks/useProjects.js';
import { useSiteConnection, usePublishSite, usePublishPlan } from '../../../hooks/useSiteConnection.js';
import { useHallazgos } from '../../../hooks/useHallazgos.js';

export function usePublicarConNota(project: ProjectRow): {
  revisarYPublicar: () => Promise<void>;
  ocupado: boolean;
  revisando: boolean;
  nota: ReactNode;
} {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: connData } = useSiteConnection(project.id);
  const conn = connData?.connection ?? null;
  const publishSite = usePublishSite(project.id);
  const plan = usePublishPlan(project.id);
  const { data: hallazgos } = useHallazgos(project.id, false);
  const [notas, setNotas] = useState<string[] | null>(null);
  const s = 'projects.design.tab.step3';

  function subir(): void {
    setNotas(null);
    publishSite.mutate(undefined, {
      onSuccess: r => {
        if (!r.ok) { toast.error(t(`projects.design.tab.step1.stage.${r.stage}`, { defaultValue: r.detail ?? '' })); return; }
        const subidos = r.aSubir?.length ?? 0;
        toast.success(subidos === 0
          ? t(`${s}.publishNothing`)
          : t(`${s}.publishResult`, { subidos, intactos: r.cambiadosAfuera?.length ?? 0, respaldo: r.respaldoEn ?? '—' }));
      },
    });
  }

  async function revisarYPublicar(): Promise<void> {
    if (!conn || project.currentVersion === 0) { toast.info(t(`${s}.publishPending`)); return; }
    const avisos: string[] = [];
    // 1. Lo que quedó a medias en Salud del sitio.
    const vigentes = new Set((hallazgos?.hallazgos ?? []).map(h => h.id));
    const guardados = hallazgos?.borradores ?? {};
    const b = Object.values(guardados).filter(x => vigentes.has(x.hallazgoId));
    const textos = b.filter(x => x.estado === 'propuesta').length;
    const decidir = b.filter(x => x.estado === 'decidir').length;
    // Lo ya aplicado en la copia de trabajo no es "sin hacer": sigue en la
    // lista porque la medición es del sitio publicado, y publicar es justo lo
    // que lo saca (Eduardo, 2026-09-06: "si ya los apliqué local, debe
    // dejarme subirlo sin decir eso").
    const automaticos = (hallazgos?.hallazgos ?? []).filter(h => h.accion === 'automatico' && guardados[h.id]?.estado !== 'aplicado').length;
    if (textos > 0) avisos.push(t(`${s}.preflight.textsPending`, { count: textos }));
    if (decidir > 0) avisos.push(t(`${s}.preflight.decidePending`, { count: decidir }));
    if (automaticos > 0) avisos.push(t(`${s}.preflight.autoPending`, { count: automaticos }));
    // 2. Lo que pasaría en el servidor, sin escribir nada.
    try {
      const r = await plan.mutateAsync();
      const aSubir = r.plan.aSubir?.length ?? 0;
      const afuera = r.plan.cambiadosAfuera?.length ?? 0;
      if (!r.plan.ok) avisos.push(t(`${s}.preflight.planFailed`, { error: t(`projects.design.tab.step1.stage.${r.plan.stage}`, { defaultValue: r.plan.detail ?? '' }) }));
      else {
        if (afuera > 0) avisos.push(t(`${s}.preflight.changedOutside`, { count: afuera }));
        if (aSubir === 0) avisos.push(t(`${s}.preflight.noChanges`));
        else if (avisos.length > 0) {
          avisos.push(t(`${s}.preflight.filesToUpload`, { count: aSubir }));
          if ((r.plan.sinCambios ?? 0) > 0) avisos.push(t(`${s}.preflight.unchanged`, { count: r.plan.sinCambios ?? 0 }));
        }
      }
    } catch (e) {
      avisos.push(t(`${s}.preflight.planFailed`, { error: e instanceof Error ? e.message : t('common.error') }));
    }
    if (avisos.length === 0) { subir(); return; }
    setNotas(avisos);
  }

  const nota = (
    <Modal
      open={notas !== null}
      onClose={() => { setNotas(null); }}
      title={t(`${s}.preflight.title`)}
      description={t(`${s}.preflight.intro`)}
      size="md"
      footer={
        <>
          <Button size="sm" variant="outline" onClick={() => { setNotas(null); }}>{t(`${s}.preflight.fix`)}</Button>
          <Button size="sm" tone="cyan" loading={publishSite.isPending} onClick={subir}>{t(`${s}.preflight.publishAnyway`)}</Button>
        </>
      }
    >
      <ul style={{ margin: 0, paddingLeft: '1.2em', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', lineHeight: 1.6 }}>
        {(notas ?? []).map(n => <li key={n}>{n}</li>)}
      </ul>
    </Modal>
  );

  return { revisarYPublicar, ocupado: plan.isPending || publishSite.isPending, revisando: plan.isPending, nota };
}
